// ---------------------------------------------------------------------------
// ConflictResolutionPanel — Shows unresolved sync conflicts and lets the
// user choose "keep local" or "accept server" for each one.
// Opened from DefensibleDataWidget when conflicts are detected.
// Mirrors tenant-web's ConflictResolutionPanel; uses useSystemDb() instead.
// ---------------------------------------------------------------------------

import { useState, useMemo } from 'react';
import {
  AlertTriangle,
  X,
  Check,
  ArrowDownToLine,
  ArrowUpFromLine,
  ChevronDown,
  ChevronRight,
  Trash2,
} from 'lucide-react';
import { useSystemDb } from './SystemDBProvider';
import type { SyncConflict } from './SystemDBProvider';
import { COLLECTION_DISPLAY_NAMES } from '@firesquirrel/local-db';

interface ConflictResolutionPanelProps {
  open: boolean;
  onClose: () => void;
}

export default function ConflictResolutionPanel({ open, onClose }: ConflictResolutionPanelProps) {
  const { conflicts, resolveConflict, dismissConflict, clearResolvedConflicts } = useSystemDb();

  const unresolvedConflicts = useMemo(
    () => conflicts.filter((c) => !c.resolved),
    [conflicts],
  );
  const resolvedConflicts = useMemo(
    () => conflicts.filter((c) => c.resolved),
    [conflicts],
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />

      {/* Panel */}
      <div className="relative ml-auto w-full max-w-lg bg-white shadow-2xl flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-500" />
            <h2 className="text-base font-semibold text-gray-900">Sync Conflicts</h2>
            {unresolvedConflicts.length > 0 && (
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-bold text-white bg-amber-500 rounded-full">
                {unresolvedConflicts.length}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {unresolvedConflicts.length === 0 && resolvedConflicts.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center px-8">
              <Check size={40} className="text-green-400 mb-3" />
              <p className="text-sm font-medium text-gray-700">No conflicts</p>
              <p className="text-xs text-gray-500 mt-1">All data is in sync with the server.</p>
            </div>
          )}

          {unresolvedConflicts.length > 0 && (
            <div className="px-4 py-3">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Needs Resolution ({unresolvedConflicts.length})
              </h3>
              <div className="space-y-2">
                {unresolvedConflicts.map((conflict) => (
                  <ConflictCard
                    key={conflict.id}
                    conflict={conflict}
                    onResolve={resolveConflict}
                    onDismiss={dismissConflict}
                  />
                ))}
              </div>
            </div>
          )}

          {resolvedConflicts.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Resolved ({resolvedConflicts.length})
                </h3>
                <button
                  onClick={clearResolvedConflicts}
                  className="text-[11px] text-gray-400 hover:text-gray-600 transition-colors"
                >
                  Clear all
                </button>
              </div>
              <div className="space-y-1.5">
                {resolvedConflicts.map((conflict) => (
                  <div
                    key={conflict.id}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 text-xs text-gray-500"
                  >
                    <Check size={12} className="text-green-500 shrink-0" />
                    <span className="truncate">
                      {COLLECTION_DISPLAY_NAMES[conflict.collection] || conflict.collection}
                      {' · '}
                      {conflict.documentId.slice(0, 8)}…
                    </span>
                    <span className="ml-auto text-[10px] text-gray-400">
                      {conflict.resolution === 'keep-local' ? 'Kept local' : 'Accepted server'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ConflictCard — individual conflict with diff view and action buttons
// ---------------------------------------------------------------------------

interface ConflictCardProps {
  conflict: SyncConflict;
  onResolve: (id: string, resolution: 'keep-local' | 'accept-server') => Promise<void>;
  onDismiss: (id: string) => void;
}

function ConflictCard({ conflict, onResolve, onDismiss }: ConflictCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [resolving, setResolving] = useState(false);

  const collectionName = COLLECTION_DISPLAY_NAMES[conflict.collection] || conflict.collection;
  const timeAgo = formatTimeAgo(conflict.detectedAt);

  const diffs = useMemo(() => {
    const allKeys = new Set([
      ...Object.keys(conflict.localState),
      ...Object.keys(conflict.serverState),
    ]);
    const result: { field: string; local: unknown; server: unknown }[] = [];
    for (const key of allKeys) {
      if (key.startsWith('_') && key !== '_id') continue;
      const local = conflict.localState[key];
      const server = conflict.serverState[key];
      if (JSON.stringify(local) !== JSON.stringify(server)) {
        result.push({ field: key, local, server });
      }
    }
    return result;
  }, [conflict]);

  async function handleResolve(resolution: 'keep-local' | 'accept-server') {
    setResolving(true);
    try {
      await onResolve(conflict.id, resolution);
    } finally {
      setResolving(false);
    }
  }

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/50 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2.5">
        <button
          onClick={() => setExpanded(!expanded)}
          className="p-0.5 rounded hover:bg-amber-100 transition-colors text-amber-600"
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <AlertTriangle size={14} className="text-amber-500 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-semibold text-gray-800">{collectionName}</span>
            <span className="text-[10px] text-gray-400">·</span>
            <span className="text-[10px] text-gray-500 font-mono truncate">
              {conflict.documentId.slice(0, 12)}
            </span>
          </div>
          <div className="text-[10px] text-gray-400 mt-0.5">
            {diffs.length} field{diffs.length !== 1 ? 's' : ''} differ · {timeAgo}
          </div>
        </div>
        <button
          onClick={() => onDismiss(conflict.id)}
          className="p-1 rounded hover:bg-amber-100 transition-colors text-gray-400 hover:text-gray-600"
          title="Dismiss"
        >
          <Trash2 size={12} />
        </button>
      </div>

      {expanded && (
        <div className="border-t border-amber-200 bg-white">
          <div className="divide-y divide-gray-100">
            {diffs.map((diff) => (
              <div key={diff.field} className="px-3 py-2">
                <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
                  {diff.field}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded bg-blue-50 px-2 py-1.5">
                    <div className="text-[9px] font-semibold text-blue-600 uppercase mb-0.5">Local</div>
                    <div className="text-[11px] text-gray-700 font-mono break-all max-h-16 overflow-y-auto">
                      {formatValue(diff.local)}
                    </div>
                  </div>
                  <div className="rounded bg-green-50 px-2 py-1.5">
                    <div className="text-[9px] font-semibold text-green-600 uppercase mb-0.5">Server</div>
                    <div className="text-[11px] text-gray-700 font-mono break-all max-h-16 overflow-y-auto">
                      {formatValue(diff.server)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {diffs.length === 0 && (
              <div className="px-3 py-2 text-xs text-gray-400 italic">
                No visible field differences (may be internal metadata).
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 px-3 py-2.5 border-t border-gray-100 bg-gray-50/50">
            <button
              onClick={() => handleResolve('keep-local')}
              disabled={resolving}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors disabled:opacity-50"
            >
              <ArrowUpFromLine size={12} />
              Keep Local
            </button>
            <button
              onClick={() => handleResolve('accept-server')}
              disabled={resolving}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 transition-colors disabled:opacity-50"
            >
              <ArrowDownToLine size={12} />
              Accept Server
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatValue(val: unknown): string {
  if (val === undefined) return '(undefined)';
  if (val === null) return '(null)';
  if (typeof val === 'string') return val.length > 100 ? val.slice(0, 100) + '…' : val;
  if (typeof val === 'object') {
    const str = JSON.stringify(val, null, 1);
    return str.length > 120 ? str.slice(0, 120) + '…' : str;
  }
  return String(val);
}

function formatTimeAgo(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  if (diff < 5000) return 'just now';
  if (diff < 60000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  return `${Math.floor(diff / 3600000)}h ago`;
}
